import buildPurchasePayload, { planId, planName, planValue } from './purchase';

/**
 * Esquema de comercio electrónico de GA4 para el dataLayer (VEX-6460).
 *
 * El problema que resuelve: cada evento empujaba el objeto crudo que devolvió
 * la API (`data.viewItem`, `data.beginCheckout`, `data.addPaymentInfo`). Tres
 * costos: nadie controla qué hay adentro, cualquier cambio de forma en la API
 * rompe la medición en silencio —que es exactamente lo que pasó con
 * `originalCost`— y no hay `items`, que es lo único que leen los informes de
 * Monetización de GA4.
 *
 * Este módulo es el contrato: entra un contenido o un plan, sale un item con
 * las claves del esquema y nada más.
 *
 * IMPORTANTE: este front y el de Astro cargan el MISMO contenedor de GTM
 * (GTM-W3R8R6G) y escriben en la misma propiedad de GA4. El mapeo de acá tiene
 * que dar el mismo item que `src/utils/analytics/ecommerce.ts` del front de
 * Astro: si los dos emiten categorías distintas para el mismo contenido, los
 * informes quedan partidos en dos y ninguno de los dos cierra.
 */

/** Tope de items por evento que acepta GA4. Más allá se descarta el evento entero. */
export const GA4_MAX_ITEMS = 200;

const BRAND = 'Vexels';

/**
 * Misma tabla que `getPreviewTypeFromIdType` del front de Astro. Se copia en
 * vez de importarse porque son dos repos, y se prueba que coincidan.
 */
const PREVIEW_TYPE_BY_ID_TYPE = {
  6: 'psd',
  7: 'png',
  8: 'editables',
  9: 'tshirt-maker',
  10: 'editable-online',
  11: 'mockup'
};

const DEFAULT_PREVIEW_TYPE = 'vector';

export const itemCategory = (content) => {
  if (!content) return undefined;
  // Los contenidos de las herramientas (t-shirt maker, generador de mockups) no
  // siempre traen `idType`: llegan marcados con `isMaker` desde store/search.
  if (content.isMaker) return content.isMockup ? 'mockup' : 'tshirt-maker';
  if (content.idType === undefined || content.idType === null) return undefined;
  return PREVIEW_TYPE_BY_ID_TYPE[Number(content.idType)] || DEFAULT_PREVIEW_TYPE;
};

/**
 * Sin id el item no se puede atribuir: `String(undefined)` entra al informe de
 * GA4 como el producto literal "undefined", donde se mezcla con los contenidos
 * rotos de todas las demás listas. Los eventos de un solo item también pasan
 * por acá, no sólo `contentsToItems`.
 */
const hasId = (content) =>
  !!content && content.id !== undefined && content.id !== null && content.id !== '';

/** Un item, o ninguno: la lista vacía es el "no medible" del esquema. */
const itemOrNone = (content, context) => (hasId(content) ? [contentToItem(content, context)] : []);

/**
 * @param {object} content contenido crudo de la API
 * @param {object=} context `{ listId, listName, index }` de la lista que lo mostró
 */
export const contentToItem = (content, context = {}) => {
  const item = {
    item_id: String(content.id),
    item_brand: BRAND
  };

  const name = content.originalName || content.name || content.title;
  if (name) item.item_name = name;

  const category = itemCategory(content);
  if (category) item.item_category = category;

  const style = content.styles && content.styles[0] && content.styles[0].name;
  if (style) item.item_category2 = style;

  if (context.listId) item.item_list_id = context.listId;
  if (context.listName) item.item_list_name = context.listName;
  if (context.index !== undefined) item.index = context.index;

  return item;
};

/**
 * `index` es la posición en la grilla y arranca en 1: es lo que se lee después
 * en los informes ("el item de la posición 1"), no un índice de array.
 */
export const contentsToItems = (contents, { listId, listName } = {}) =>
  (contents || [])
    .filter(hasId)
    .slice(0, GA4_MAX_ITEMS)
    .map((content, i) => contentToItem(content, { listId, listName, index: i + 1 }));

/**
 * Un plan como item de GA4. El importe sale de `planValue`, que ya sabe leer
 * las dos formas que puede tener la cookie `vx.plan` (`originalCost` del plan
 * de catálogo o `planPrice` de la suscripción activa). Ver `purchase.js`.
 */
export const planToItem = (plan) => {
  const id = planId(plan);
  const name = planName(plan);
  const value = planValue(plan);

  const item = { item_brand: BRAND, quantity: 1 };
  if (id !== undefined) item.item_id = String(id);
  if (name !== undefined) item.item_name = name;
  if (plan && plan.chargeFrecuency) item.item_category = plan.chargeFrecuency;
  if (value !== undefined) item.price = value;

  return item;
};

const planEcommerce = (plan) => {
  const value = planValue(plan);
  return {
    currency: 'USD',
    // Sin importe usable el evento sale SIN `value` en vez de con 0: un cero se
    // suma en los informes como si fuera un checkout de cero dólares.
    ...(value === undefined ? {} : { value }),
    items: plan ? [planToItem(plan)] : []
  };
};

/**
 * Durante la migración cada evento empuja LAS DOS FORMAS: el `ecommerce` nuevo
 * y el `data.loQueSea` viejo. Las etiquetas publicadas hoy siguen leyendo lo
 * viejo mientras se arman las nuevas; recién cuando todas migraron se borra el
 * bloque `data` y las variables de capa de datos que queden sin uso. Es el
 * mismo criterio que ya se usó en `buildPurchasePayload`.
 */
export const viewItemPayload = (content) => ({
  data: { viewItem: content },
  ecommerce: { items: itemOrNone(content) }
});

export const beginCheckoutPayload = (plan) => ({
  data: { beginCheckout: plan },
  ecommerce: planEcommerce(plan)
});

export const addPaymentInfoPayload = (plan) => ({
  data: { addPaymentInfo: plan },
  ecommerce: planEcommerce(plan)
});

/**
 * El `purchase` ya venía con forma controlada desde VEX-6438: `buildPurchasePayload`
 * arma en un solo objeto los campos canónicos de GA4 y los de compatibilidad con
 * la etiqueta publicada. Acá se parte en dos: los canónicos suben al bloque
 * `ecommerce` de la raíz y el objeto entero se sigue empujando como
 * `data.purchase`, que es de donde lo lee la etiqueta de hoy.
 */
export const purchasePayload = ({ plan, paymentIntent, transactionId, provider } = {}) => {
  const purchase = buildPurchasePayload({ plan, paymentIntent, transactionId, provider });

  const ecommerce = {
    transaction_id: purchase.transaction_id,
    currency: purchase.currency,
    payment_type: purchase.payment_type,
    items: purchase.items
  };
  // Igual que en el checkout: sin importe el evento sale SIN `value` en vez de
  // con 0, para no sumar ingreso inexistente.
  if (purchase.value !== undefined) ecommerce.value = purchase.value;

  return { data: { purchase }, ecommerce };
};

export const viewItemListPayload = ({ contents, listId, listName }) => ({
  data: { itemListId: listId, itemListName: listName },
  ecommerce: {
    item_list_id: listId,
    item_list_name: listName,
    items: contentsToItems(contents, { listId, listName })
  }
});

export const selectItemPayload = ({ content, index, listId, listName }) => ({
  data: { itemListId: listId, itemListName: listName },
  ecommerce: {
    item_list_id: listId,
    item_list_name: listName,
    items: itemOrNone(content, { listId, listName, index })
  }
});

/**
 * Las listas que mide este front. Los ids son ESTABLES entre deploys a
 * propósito: uno que cambia parte la serie histórica del informe.
 *
 * Los nombres son de cardinalidad baja (no llevan la keyword buscada ni el
 * nombre de la colección): `item_list_name` es la etiqueta del informe, no la
 * del contenido.
 */
export const ITEM_LISTS = {
  searchResults: { id: 'search_results', name: 'Search results' },
  sortSearch: { id: 'sort_search', name: 'Sorted search' },
  collection: { id: 'collection', name: 'Collection' },
  style: { id: 'style_detail', name: 'Style detail' },
  category: { id: 'category', name: 'Category' },
  niche: { id: 'niche', name: 'Niche' },
  tshirtMaker: { id: 'tshirt_maker_templates', name: 'T-shirt maker templates' },
  mockupGenerator: { id: 'mockup_generator_templates', name: 'Mockup generator templates' }
};
